import type { ProjectElementNode } from '@deltek/specification-client/core/Models/productListingDetail'
import type { UniformatClassificationProperty } from '@deltek/specification-client/core/Models/uniformatClassificationProperty'
import Typography from '@material-ui/core/Typography'
import { Tree } from 'antd'
import { PROPERTY_EDITOR_EDITABLE_TEXT_VARIANT as NODE_TYPES } from 'pages/ProjectsPage/WorkspacePage/components/constants'
import { getAssemblyLevel } from 'pages/ProjectsPage/WorkspacePage/components/ElementsPanel/ElementsPanel'
import React, { Key, memo, useCallback, useEffect, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { NodeContentRenderer, SwitcherIcon } from '.'
import { useResizeObserver } from '../hooks'
import { TransformedNode } from '../hooks/useTreeDataTransformer'
import { useAssemblyStyles } from '../styles'
import type { DataNode } from '../types/types'
import { useExpandedKeys, useWorkspace2Actions } from '../Workspace2Store'
import ProjectProperties from './ProjectProperties'
import { TreeLayout } from './TreeView'
import WorkspaceTitle from './WorkspaceTitle'

/** The props for the buildTreeNodes function */
interface BuildTreeNodesProps {
    /** The nodes to build the tree from */
    nodes: DataNode[]
    /** The keys of nodes that are expanded */
    expandedKeys: Key[]
    /** The CSS classes used for styling the TreeView */
    treeViewClasses: any
    /** Whether the nodes should be editable */
    editable: boolean
}

/**
 * Recursively builds a tree data structure from an array of DataNodes.
 * Also sets 'data-testid' and 'data-key' attributes for testing and debugging.
 * @param nodes - Array of DataNodes to build the tree from.
 * @param expandedKeys - Array of keys of nodes that are expanded.
 * @param treeViewClasses - Object containing CSS classes used for styling the TreeView.
 * @returns Transformed tree data structure.
 */
const buildTreeNodes = ({
    nodes = [],
    expandedKeys = [],
    treeViewClasses,
    editable,
}: BuildTreeNodesProps): TransformedNode[] => {
    return nodes.map((node) => ({
        ...node,
        'data-testid': 'tree-child',
        'data-key': node.key,
        className: `${
            node.type === NODE_TYPES.Article
                ? treeViewClasses.articleContainer
                : ''
        } ${treeViewClasses.accordionStyle}`,
        switcherIcon: (
            <SwitcherIcon
                type={node.type}
                isLoading={false}
                isExpanded={expandedKeys?.includes(node.key)}
                node={node}
            />
        ),
        title: <NodeContentRenderer node={node} editable={editable} />,
        children: buildTreeNodes({
            nodes: node.children || [],
            expandedKeys,
            treeViewClasses,
            editable,
        }),
    }))
}

/** Props for TreeView component */
interface TreeViewProps {
    classes: any
    /** Project ID */
    projectId: string
    /** The selected project element node */
    selectedProjectElementNode: ProjectElementNode
    /** Transformed tree data for Ant Design Tree */
    treeData: TransformedNode[]
    /** Keys of expanded nodes */
    expandedKeys: Key[]
    /** Handler for tree expansion */
    onExpand: (newExpandedKeys: Key[]) => void
}

/**
 * Tree view component for ReviewAssembly
 * Renders the tree structure with header components and virtual scrolling
 */
export const TreeView = memo<TreeViewProps>(
    ({
        classes,
        projectId,
        selectedProjectElementNode,
        treeData,
        expandedKeys,
        onExpand,
    }) => {
        const resizeObserver = useResizeObserver()

        return (
            <div className={classes.workspaceEditorContainer}>
                {/* Header Section with WorkspaceTitle and ProjectProperties */}
                <WorkspaceTitle projectId={projectId} />
                <ProjectProperties
                    projectElementNode={selectedProjectElementNode}
                    projectId={projectId}
                />
                <TreeLayout
                    className={classes.treeContainer}
                    ref={resizeObserver?.treeContainerRef}
                >
                    <Tree
                        data-testid="ant-tree"
                        treeData={treeData}
                        onExpand={onExpand}
                        expandedKeys={expandedKeys}
                        className={classes.tree}
                        itemHeight={50}
                        itemScrollOffset={50}
                        virtual={true}
                        height={resizeObserver?.containerHeight}
                    />
                </TreeLayout>
            </div>
        )
    }
)


/** Props for ReviewAssembly component */
interface ReviewAssemblyProps {
    /** The selected project element node containing assembly information */
    selectedProjectElementNode?: Partial<ProjectElementNode> | null
    /** List of uniformat classification properties associated with the assembly */
    uniformatClassificationProperties?:
        | Partial<UniformatClassificationProperty>[]
        | undefined
}

/**
 * Tree view for reviewing uniformat classification properties
 * Shows different content based on assembly level (1=tree, 2-3=parent message, division=no properties)
 */
export const ReviewAssembly = memo<ReviewAssemblyProps>(
    ({ selectedProjectElementNode, uniformatClassificationProperties }) => {
        const expandedKeys = useExpandedKeys()
        const {
            setExpandedKeys,
            setClassificationNodes,
        } = useWorkspace2Actions()
        const classes = useAssemblyStyles()
        const { projectId } = useParams<{ projectId: string }>()
        const assemblyText = selectedProjectElementNode?.text ?? ''
        const isEditable = selectedProjectElementNode?.isInProject

        // Extract assembly info
        const assemblyCode = assemblyText.split('|')[0] || ''
        const assemblyName = assemblyText.split('|')[1] || assemblyText

        /** Gets assembly level from text (1=detailed, 2-3=intermediate, division=2chars) */
        const assemblyLevel = useMemo(() => getAssemblyLevel(assemblyCode), [
            assemblyCode,
        ])

        /** Checks if assembly code is division level (2 characters) */
        const isDivisionLevel = useMemo(() => assemblyCode?.length === 2, [
            assemblyCode,
        ])

        /** Builds tree structure from uniformat properties */
        const classificationNodes: DataNode[] = useMemo(() => {
            return (
                uniformatClassificationProperties?.map((element) => ({
                    key: element.id ?? '',
                    type: NODE_TYPES.Article,
                    order: 0,
                    contentItemNodes: [
                        {
                            id: element.id ?? '',
                            text: element.name ?? '',
                            type: NODE_TYPES.Article,
                            order: 0,
                        },
                    ],
                    children: [
                        {
                            key: `${element.id ?? ''}_property`,
                            type: NODE_TYPES.AssemblyText,
                            order: 0,
                            contentItemNodes: [
                                {
                                    id: element.id ?? '',
                                    text: element.value ?? '',
                                    placeholderText: element.hint ?? '',
                                    type: NODE_TYPES.AssemblyText,
                                    order: 0,
                                },
                            ],
                        },
                    ],
                })) || []
            )
        }, [uniformatClassificationProperties])

        useEffect(() => {
            if (classificationNodes.length > 0) {
                setExpandedKeys(classificationNodes.map((node) => node.key))
                setClassificationNodes(classificationNodes)
            }
        }, [classificationNodes, setClassificationNodes, setExpandedKeys])

        /** Transforms nodes for Ant Design Tree component */
        const treeData = useMemo(
            () =>
                buildTreeNodes({
                    nodes: classificationNodes,
                    expandedKeys,
                    treeViewClasses: classes,
                    editable: !!isEditable,
                }),
            [classificationNodes, expandedKeys, classes, isEditable]
        )

        /** Handles tree expansion and updates global state */
        const handleExpand = useCallback(
            (newExpandedKeys: Key[]): void => {
                setExpandedKeys(newExpandedKeys)
            },
            [setExpandedKeys]
        )

        /** Checks if assembly has classification properties to display */
        const hasProperties = useMemo(() => classificationNodes?.length > 0, [
            classificationNodes,
        ])

        /** Shows message for level 2-3, division, or level 1 without properties */
        const shouldShowMessage = useMemo(
            () =>
                [2, 3].includes(assemblyLevel) ||
                isDivisionLevel ||
                !hasProperties,
            [assemblyLevel, isDivisionLevel, hasProperties]
        )

        /** Shows tree only for level 1 assemblies with properties (not division level) */
        const shouldShowTree = useMemo(
            () => assemblyLevel === 1 && hasProperties && !isDivisionLevel,
            [assemblyLevel, hasProperties, isDivisionLevel]
        )

        return (
            <div
                style={{ height: '100%', paddingTop: '52px' }}
                data-testid="review-assembly"
            >
                {/* Content based on assembly level */}
                {shouldShowMessage && (
                    <div className={classes.assemblyLabelContainer}>
                        <Typography
                            component="span"
                            className={classes.grayText}
                            display="block"
                        >
                            {isDivisionLevel || assemblyLevel === 1
                                ? 'No properties associated found'
                                : 'Select the Parent of this Subassembly in order to view Performance Requirements. Assembly parents are designated with 5 characters (ex.A1010)'}
                        </Typography>
                    </div>
                )}

                {shouldShowTree && (
                    <TreeView
                        classes={classes}
                        projectId={projectId || ''}
                        selectedProjectElementNode={
                            selectedProjectElementNode as ProjectElementNode
                        }
                        treeData={treeData}
                        expandedKeys={expandedKeys}
                        onExpand={handleExpand}
                    />
                )}
            </div>
        )
    }
)
